import { useState, type ReactNode } from 'react';
import { color, font, severityTheme, stageTheme, topicColors } from '../styles/theme';
import { num, shortDate, headerDate } from '../lib/format';
import { makeLookup, summaryOf } from '../lib/presenters';
import { statusOptions } from '../lib/pipeline';
import { messageForms, messageStatusOptions } from '../lib/messageTypes';
import { useOverview, useAnalytics, useNewsletter, useMeta, useSubmissions, useSetStatus } from '../api/queries';
import { downloadCsv } from '../api/client';
import { useClockSummary, type ClockSummary } from '../api/operations';
import { useAuth } from '../auth/AuthContext';
import { Card, CardIcon, CardTitle, ColHead, Loading, ErrorNote, Meter, selectStyle } from '../components/ui';
import {
  IconEye,
  IconTarget,
  IconInquiries,
  IconAdmins,
  IconTrend,
  IconSortArrows,
  IconCalendar,
  IconDownload,
  IconCheck,
  IconSend,
  IconArrowRight,
} from '../components/icons';
import { SubmissionDetail } from './SubmissionDetail';
import { NoLetterNote, NOT_A_MATCH } from './Conversation';
import { RedcapWeekCard } from './RedcapWeekCard';
import type { Section } from '../components/Shell';

const MONTH_PLOT = 190;

/** Chip used under the big figures — green when the trend is up. */
function Delta({ children, tone = 'up' }: { children: ReactNode; tone?: 'up' | 'flat' }) {
  return (
    <span
      style={{
        background: tone === 'up' ? '#d9ebdf' : color.line,
        color: tone === 'up' ? '#14563c' : color.muted,
        fontWeight: 800,
        fontSize: 11,
        padding: '3px 9px',
        borderRadius: 999,
      }}
    >
      {children}
    </span>
  );
}

/**
 * Every running reply clock, counted (GET /tickets/summary). Past due and due
 * within four hours take the safety badges' colours only when there are some:
 * a red zero would be an alarm about nothing, and the eye should go straight
 * to the one figure that needs someone.
 */
function ClockCard({ summary }: { summary: ClockSummary }) {
  const figures: Array<{ label: string; value: number; tone?: { bg: string; fg: string } }> = [
    { label: 'Running', value: summary.open },
    {
      label: 'Past due',
      value: summary.breached,
      tone: summary.breached > 0 ? severityTheme['urgent-today'] : undefined,
    },
    {
      label: 'Due within 4 hours',
      value: summary.atRisk,
      tone: summary.atRisk > 0 ? severityTheme.watch : undefined,
    },
    { label: 'On hold', value: summary.onHold },
    { label: 'No owner', value: summary.unassigned },
  ];
  return (
    <Card pad="20px 22px">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <CardIcon>
          <IconCalendar />
        </CardIcon>
        <span style={{ fontSize: 14.5, fontWeight: 800, color: color.ink, flex: 1 }}>Reply deadlines</span>
        <span style={{ fontSize: 12, color: color.muted }}>Every form, right now</span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(118px,1fr))',
          gap: 10,
          marginTop: 14,
        }}
      >
        {figures.map((f) => (
          <div
            key={f.label}
            style={{ background: f.tone?.bg ?? color.bg, borderRadius: 14, padding: '12px 14px' }}
          >
            <p style={{ fontSize: 12, fontWeight: 700, color: f.tone?.fg ?? color.muted, margin: 0 }}>
              {f.label}
            </p>
            <p
              style={{
                fontFamily: font.display,
                fontWeight: 600,
                fontSize: 19,
                color: f.tone?.fg ?? color.navy,
                margin: '6px 0 0',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {num(f.value)}
            </p>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 12, color: color.muted, margin: '12px 0 0' }}>
        One clock per reply the lab owes, started when a form arrives. Past due and due within 4 hours count clocks
        on hold too.
      </p>
    </Card>
  );
}

function BigStat({
  icon,
  label,
  value,
  chip,
  note,
  action,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  chip?: string;
  note?: string;
  action?: { title: string; onClick: () => void };
}) {
  return (
    <Card pad="20px 22px">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <CardIcon>{icon}</CardIcon>
        <span style={{ fontSize: 14.5, fontWeight: 800, color: color.ink, flex: 1 }}>{label}</span>
        {action && (
          <button
            onClick={action.onClick}
            title={action.title}
            className="hov-tint"
            style={{
              background: 'transparent',
              border: 'none',
              color: color.muted,
              fontSize: 16,
              fontWeight: 800,
              cursor: 'pointer',
              padding: '2px 8px',
              borderRadius: 8,
              lineHeight: 1,
            }}
          >
            ···
          </button>
        )}
      </div>
      <p
        style={{
          fontFamily: font.display,
          fontWeight: 600,
          fontSize: 32,
          lineHeight: 1,
          color: color.navy,
          margin: '16px 0 0',
        }}
      >
        {value}
      </p>
      {(chip || note) && (
        <p
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12,
            color: color.muted,
            margin: '12px 0 0',
          }}
        >
          {chip && <Delta>{chip}</Delta>}
          {note}
        </p>
      )}
    </Card>
  );
}

export function Overview({
  onSection,
  onReporting,
  tabs,
  onOpenRedcap,
}: {
  onSection: (s: Section) => void;
  /** Open one study's REDCap week — the "REDCap this week" card. */
  onOpenRedcap?: (study: string, tab: 'completeness') => void;
  /** Site reporting is a tab of this screen, not a sidebar entry. */
  onReporting: () => void;
  /** The section's own tab switch, rendered under the greeting. */
  tabs?: ReactNode;
}) {
  const { admin, can } = useAuth();
  // This tab opens on submissions:read (Home); every other card is behind its
  // own read, and a role without it gets no card rather than a 403.
  const seesTraffic = can('analytics:read');
  const seesNewsletter = can('newsletter:read');
  const meta = useMeta();
  const overview = useOverview();
  const analytics = useAnalytics(seesTraffic);
  const newsletter = useNewsletter(seesNewsletter);
  const clocks = useClockSummary(seesTraffic);
  const setStatus = useSetStatus();
  // Set after a row moves to Not a match from here: nothing is emailed, so say whose the letter is.
  const [noLetter, setNoLetter] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const recent = useSubmissions({
    tab: 'inquiry',
    status: 'all',
    q: '',
    sort: 'newest',
    page: 1,
    pageSize: 5,
  });

  if (overview.isLoading || meta.isLoading) return <Loading />;
  if (overview.error) return <ErrorNote message={(overview.error as Error).message} />;
  if (!overview.data || !meta.data) return null;

  const o = overview.data;
  const L = makeLookup(meta.data);
  const a = analytics.data;
  const firstName = admin?.name.split(' ')[0] ?? 'there';
  const visits = a?.visits.reduce((x, y) => x + y, 0) ?? 0;
  const ladder = meta.data.statuses;
  // A message offers only the statuses a message can be in (lib/messageTypes.ts).
  const messageKeys = new Set(messageForms(meta.data));

  // The design's twelve-month chart. The API serves 30 days of dailies and a
  // weekly rollup, so the trend row is the weeks it does have, with the newest
  // column highlighted the way the design highlights the current month.
  const weeks = a?.weeks ?? [];
  const weekMax = Math.max(...weeks.map((w) => w.visits), 1);

  const sources = (a?.sources ?? []).slice(0, 4);

  // General inquiries still waiting on the lab — the same `fresh` count the
  // Messages badge is built from. `stageCounts.new` was every form's rows in
  // stage New, so an intake moved a card that says inquiries.
  const newInquiries = o.formStats.find((f) => f.key === 'inquiry')?.fresh ?? 0;

  async function exportCsv() {
    setExporting(true);
    setExportError(null);
    try {
      await downloadCsv(
        '/submissions/export.csv',
        { tab: 'all', status: 'all', q: '', sort: 'newest' },
        'enact-submissions.csv',
      );
    } catch (err) {
      // The API's own sentence: the hourly limit, a refused permission, the
      // export being unavailable. Saying nothing read as a download that never came.
      setExportError(err instanceof Error ? err.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} className="fade-in">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h1
            style={{
              fontFamily: font.display,
              fontWeight: 600,
              fontSize: 27,
              color: color.navy,
              margin: 0,
            }}
          >
            Welcome back, {firstName}
          </h1>
          <p style={{ fontSize: 13.5, color: color.muted, margin: '5px 0 0' }}>
            Monitor and control what's happening at ENACT this month.
          </p>
          {tabs && <div style={{ marginTop: 12 }}>{tabs}</div>}
        </div>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: color.white,
            border: `1px solid ${color.line}`,
            borderRadius: 999,
            padding: '9px 17px',
            fontSize: 13,
            fontWeight: 700,
            color: color.ink,
          }}
        >
          <span style={{ display: 'flex', color: color.muted }}>
            <IconCalendar />
          </span>
          {headerDate()}
        </span>
        {can('submissions:export') && (
          <button
            onClick={() => void exportCsv()}
            disabled={exporting}
            className="hov-navy"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: color.navy,
              color: color.white,
              border: 'none',
              fontSize: 13,
              fontWeight: 700,
              padding: '10px 20px',
              borderRadius: 999,
              cursor: exporting ? 'not-allowed' : 'pointer',
            }}
          >
            <IconDownload /> {exporting ? 'Preparing…' : 'Export'}
          </button>
        )}
      </div>

      {exportError && <ErrorNote message={exportError} />}
      {setStatus.error && <ErrorNote message={(setStatus.error as Error).message} />}
      {noLetter && <NoLetterNote after />}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
        {/* ── Left rail: traffic and enrollment ── */}
        <div
          style={{
            flex: '1 1 300px',
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minWidth: 0,
          }}
        >
          {/* What each linked study has to talk about this Monday; nothing when no study is linked. */}
          {onOpenRedcap && can('studies:read') && (
            <RedcapWeekCard onOpen={(study) => onOpenRedcap(study, 'completeness')} />
          )}
          {seesTraffic && (
          <Card pad="20px 22px">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <CardIcon>
                <IconEye />
              </CardIcon>
              <span style={{ fontSize: 14.5, fontWeight: 800, color: color.ink, flex: 1 }}>
                Total visits
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: color.muted,
                  border: `1px solid ${color.line}`,
                  borderRadius: 999,
                  padding: '5px 12px',
                }}
              >
                Last 30 days
              </span>
            </div>
            <p
              style={{
                fontFamily: font.display,
                fontWeight: 600,
                fontSize: 36,
                lineHeight: 1,
                color: color.navy,
                margin: '18px 0 0',
              }}
            >
              {num(visits)}
            </p>
            {a?.cards[0] && (
              <p
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 12,
                  color: color.muted,
                  margin: '12px 0 0',
                }}
              >
                <Delta tone={a.cards[0].tone === 'up' ? 'up' : 'flat'}>{a.cards[0].delta}</Delta>
              </p>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button
                onClick={() => onSection('messages')}
                className="hov-blue"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 7,
                  background: color.blue,
                  color: color.white,
                  border: 'none',
                  fontSize: 13,
                  fontWeight: 700,
                  padding: '11px 0',
                  borderRadius: 999,
                  cursor: 'pointer',
                }}
              >
                <IconCheck /> Review messages
              </button>
              {seesNewsletter && (
              <button
                onClick={() => onSection('newsletter')}
                className="hov-tint2"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 7,
                  background: color.tint,
                  color: color.blueDeep,
                  border: 'none',
                  fontSize: 13,
                  fontWeight: 700,
                  padding: '11px 0',
                  borderRadius: 999,
                  cursor: 'pointer',
                }}
              >
                <IconSend /> Send newsletter
              </button>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', margin: '22px 0 12px' }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: color.ink, flex: 1 }}>
                Traffic sources
              </span>
              <button
                onClick={onReporting}
                className="hov-tint"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: color.blue,
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: 8,
                }}
              >
                View all
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {sources.map((s, i) => (
                <div key={s.label} style={{ background: color.bg, borderRadius: 14, padding: '12px 14px' }}>
                  <p
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 7,
                      fontSize: 12,
                      fontWeight: 700,
                      color: color.muted,
                      margin: 0,
                    }}
                  >
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: topicColors[i % topicColors.length],
                        flex: 'none',
                      }}
                    />
                    {s.label}
                  </p>
                  <p
                    style={{
                      fontFamily: font.display,
                      fontWeight: 600,
                      fontSize: 19,
                      color: color.navy,
                      margin: '8px 0 2px',
                    }}
                  >
                    {s.share}%
                  </p>
                  <p style={{ fontSize: 11, color: color.muted, margin: 0 }}>of visits</p>
                </div>
              ))}
              {sources.length === 0 && (
                <p style={{ fontSize: 12.5, color: color.muted, margin: 0 }}>
                  No traffic data yet.
                </p>
              )}
            </div>
          </Card>
          )}

          <Card pad="20px 22px">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <CardIcon>
                <IconTarget />
              </CardIcon>
              <span style={{ fontSize: 14.5, fontWeight: 800, color: color.ink, flex: 1 }}>
                Per-form volume
              </span>
              <button
                onClick={() => onSection('forms')}
                className="hov-tint"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: color.blue,
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: 8,
                }}
              >
                View all
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {o.formStats.map((f) => {
                const share = o.total > 0 ? Math.round((f.total / o.total) * 100) : 0;
                return (
                  <div key={f.key}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span
                        style={{
                          display: 'flex',
                          width: 32,
                          height: 32,
                          borderRadius: 10,
                          background: color.tint,
                          color: color.blue,
                          alignItems: 'center',
                          justifyContent: 'center',
                          flex: 'none',
                          fontSize: 12,
                          fontWeight: 800,
                        }}
                      >
                        {f.short.slice(0, 2).toUpperCase()}
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display: 'block',
                            fontSize: 13.5,
                            fontWeight: 800,
                            color: color.ink,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {f.short}
                        </span>
                        <span style={{ display: 'block', fontSize: 11.5, color: color.muted }}>
                          {f.fresh} waiting · {f.sla}
                        </span>
                      </span>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 800,
                          color: color.navy,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {f.total}
                      </span>
                    </div>
                    <div style={{ marginTop: 9 }}>
                      <Meter pct={share} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* ── Right column: counters, trend, recent inquiries ── */}
        <div
          style={{
            flex: '2 1 480px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))',
              gap: 16,
            }}
          >
            <BigStat
              icon={<IconInquiries size={17} />}
              label="New inquiries"
              value={num(newInquiries)}
              note="General inquiries awaiting first action"
              action={{ title: 'Open messages', onClick: () => onSection('messages') }}
            />
            {seesNewsletter && (
            <BigStat
              icon={<IconAdmins size={17} />}
              label="Subscribers"
              value={num(newsletter.data?.subscriberCount ?? 0)}
              // A green "+0 ↑" reads as growth; say plainly that there was none.
              chip={newsletter.data?.growth30d ? `+${newsletter.data.growth30d} ↑` : undefined}
              note={
                newsletter.data && newsletter.data.growth30d === 0
                  ? 'None new in the last 30 days'
                  : 'from last month'
              }
              action={{ title: 'Open newsletter', onClick: () => onSection('newsletter') }}
            />
            )}
          </div>

          {clocks.data && <ClockCard summary={clocks.data} />}

          {seesTraffic && (
          <Card pad="20px 24px 18px">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <CardIcon>
                <IconTrend />
              </CardIcon>
              <CardTitle style={{ flex: 1 }}>Visits overview</CardTitle>
              {weeks.length > 0 && (
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    fontSize: 12,
                    fontWeight: 700,
                    color: color.muted,
                  }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: color.blue }} />
                  Visits
                </span>
              )}
              {weeks.length > 0 && (
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: color.ink,
                    border: `1px solid ${color.line}`,
                    borderRadius: 999,
                    padding: '5px 13px',
                  }}
                >
                  Last {weeks.length} {weeks.length === 1 ? 'week' : 'weeks'}
                </span>
              )}
            </div>

            {/* No provider connected, or nothing counted yet: say so rather than
                drawing an empty axis whose rounded ticks read 1, 1, 0, 0. */}
            {weeks.length === 0 ? (
              <p style={{ fontSize: 13.5, color: color.muted, margin: '18px 0 2px' }}>
                No visit data yet.
              </p>
            ) : (
            <div style={{ display: 'flex', marginTop: 20 }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  height: MONTH_PLOT,
                  paddingRight: 12,
                  flex: 'none',
                }}
              >
                {[weekMax, Math.round(weekMax * 0.66), Math.round(weekMax * 0.33), 0].map((v, i) => (
                  <span
                    key={i}
                    style={{ fontSize: 11, color: color.muted, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {num(v)}
                  </span>
                ))}
              </div>

              <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    height: MONTH_PLOT,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    pointerEvents: 'none',
                  }}
                >
                  {[0, 1, 2, 3].map((i) => (
                    <span
                      key={i}
                      style={{
                        borderTop: i === 3 ? `1px solid ${color.line}` : `1px dashed ${color.line}`,
                      }}
                    />
                  ))}
                </div>

                <div
                  style={{
                    position: 'relative',
                    height: MONTH_PLOT,
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 10,
                  }}
                >
                  {weeks.map((w, i) => {
                    const last = i === weeks.length - 1;
                    return (
                      <div
                        key={w.label}
                        title={`${w.label} — ${num(w.visits)} visits`}
                        className={last ? undefined : 'hov-bar'}
                        style={{
                          flex: 1,
                          // Pixel heights, not percentages: the parent is an
                          // auto-height flex item, so a percentage resolves
                          // against nothing and every bar collapses flat.
                          height: Math.max(4, Math.round((w.visits / weekMax) * MONTH_PLOT)),
                          background: last ? color.blue : color.tint2,
                          borderRadius: '8px 8px 3px 3px',
                          minWidth: 0,
                          position: 'relative',
                        }}
                      >
                        {last && (
                          <span
                            style={{
                              position: 'absolute',
                              top: -5,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: 11,
                              height: 11,
                              borderRadius: '50%',
                              background: color.blue,
                              border: `2.5px solid ${color.white}`,
                              boxShadow: '0 2px 6px rgba(18,38,63,0.3)',
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                  {weeks.map((w, i) => {
                    const last = i === weeks.length - 1;
                    return (
                      <span
                        key={w.label}
                        style={{
                          flex: 1,
                          textAlign: 'center',
                          fontSize: 10.5,
                          fontWeight: last ? 800 : 600,
                          color: last ? color.navy : color.muted,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {w.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
            )}
          </Card>
          )}

          <Card pad={0} style={{ overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 22px 14px' }}>
              <CardIcon>
                <IconSortArrows />
              </CardIcon>
              <CardTitle style={{ flex: 1 }}>Recent inquiries</CardTitle>
              <button
                onClick={() => onSection('messages')}
                className="hov-line"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  background: 'transparent',
                  border: `1px solid ${color.line}`,
                  color: color.ink,
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                  padding: '7px 15px',
                  borderRadius: 999,
                }}
              >
                View all <IconArrowRight />
              </button>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0,2.2fr) minmax(0,1fr) minmax(0,0.9fr) minmax(70px,1.1fr) 32px',
                gap: 10,
                padding: '10px 22px',
                background: color.bg,
              }}
            >
              <ColHead>From</ColHead>
              <ColHead>Date</ColHead>
              <ColHead>Type</ColHead>
              <ColHead>Status</ColHead>
              <span />
            </div>

            {(recent.data?.rows ?? []).map((r) => {
              const st = stageTheme[r.stage];
              const d = r.data as { topic?: string };
              return (
                <div
                  key={r.id}
                  className="hov-row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      'minmax(0,2.2fr) minmax(0,1fr) minmax(0,0.9fr) minmax(70px,1.1fr) 32px',
                    gap: 10,
                    alignItems: 'center',
                    padding: '11px 22px',
                    borderBottom: `1px solid ${color.line}`,
                  }}
                >
                  <button
                    onClick={() => setSelected(r.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 11,
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      textAlign: 'left',
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        width: 32,
                        height: 32,
                        borderRadius: 10,
                        background: color.tint,
                        color: color.blueDeep,
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 800,
                        flex: 'none',
                      }}
                    >
                      {r.name.charAt(0).toUpperCase()}
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span
                        style={{
                          display: 'block',
                          fontSize: 13.5,
                          fontWeight: 700,
                          color: color.ink,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {r.name}
                      </span>
                      <span
                        style={{
                          display: 'block',
                          fontSize: 11.5,
                          color: color.muted,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {summaryOf(r, L)}
                      </span>
                    </span>
                  </button>

                  <span style={{ fontSize: 12.5, color: color.muted, whiteSpace: 'nowrap' }}>
                    {shortDate(r.createdAt)}
                  </span>

                  <span
                    style={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: color.ink,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minWidth: 0,
                    }}
                  >
                    {L.topicLabel(d.topic)}
                  </span>

                  <span style={{ minWidth: 0 }}>
                    <select
                      value={r.status}
                      disabled={!can('submissions:update') || setStatus.isPending}
                      onChange={(e) => {
                        const status = e.target.value;
                        setStatus.mutate({ id: r.id, status }, { onSuccess: () => setNoLetter(status === NOT_A_MATCH) });
                      }}
                      style={{
                        ...selectStyle,
                        border: 'none',
                        borderRadius: 999,
                        padding: '6px 24px 6px 13px',
                        fontSize: 11.5,
                        fontWeight: 800,
                        background: st.bg,
                        color: st.fg,
                        backgroundImage: selectStyle.backgroundImage,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 9px center',
                        maxWidth: '100%',
                      }}
                    >
                      {(messageKeys.has(r.form)
                        ? messageStatusOptions(ladder, r.status)
                        : statusOptions(ladder, r.status)
                      ).map((s) => (
                        <option key={s.key} value={s.key}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </span>

                  <button
                    onClick={() => setSelected(r.id)}
                    title="Details"
                    className="hov-tint2"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: color.muted,
                      fontSize: 17,
                      fontWeight: 800,
                      cursor: 'pointer',
                      padding: 4,
                      borderRadius: 8,
                      lineHeight: 1,
                    }}
                  >
                    ···
                  </button>
                </div>
              );
            })}

            {recent.data?.rows.length === 0 && (
              <p style={{ padding: '24px 22px', fontSize: 13.5, color: color.muted, margin: 0 }}>
                No inquiries yet.
              </p>
            )}
          </Card>
        </div>
      </div>

      {selected && <SubmissionDetail id={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
